import React from 'react';
import { useSelector } from 'react-redux';
import { selectPlans } from '../store/slices/pricingPlanSlice.js';
import ServiceCard from './ServiceCard.js';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import grassbackground from '../assets/services-turf.jpg';
import pathbackground from '../assets/services-bareground.jpg';
import grasslandbackground from '../assets/services-noxious.jpg';
import AnimateOnScroll from './AnimateOnScroll.js';
import PageHero from './PageHero.js';
import servicesHero from '../assets/services-hero.jpg';

const serviceImages = [
  { src: grassbackground, alt: 'Selective turf weed management on healthy green grass', width: 1024, height: 768 },
  { src: pathbackground, alt: 'Bare ground vegetation control on industrial gravel site', width: 1024, height: 768 },
  { src: grasslandbackground, alt: 'Noxious weed control treatment in BC grasslands', width: 1024, height: 768 },
];

const specialtyServices = [
  {
    title: 'Aquatic Vegetation Management',
    description: 'BC-approved herbicide application for controlling invasive aquatic vegetation in drainage ditches and retention ponds.',
    icon: (
      <svg className="w-6 h-6 text-green-700" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 2C12 2 7 8.5 7 13a5 5 0 0 0 10 0c0-4.5-5-11-5-11Z" />
      </svg>
    ),
  },
  {
    title: 'High-Rail Herbicide Application',
    description: 'Railroad vegetation control using hi-rail equipped spray vehicles. Ballast weed elimination, yard management, crossing visibility, and right-of-way programs with GPS-tracked precision.',
    icon: (
      <svg className="w-6 h-6 text-green-700" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 18h8M8 18l-2 3m10-3 2 3M6 6h12v8a4 4 0 0 1-4 4H10a4 4 0 0 1-4-4V6Zm0 0V4h12v2M9 10h6" />
      </svg>
    ),
  },
  {
    title: 'Sidewalk, Brickwork & Hardscape',
    description: 'Precision herbicide treatment for pavement joints, expansion cracks, and brickwork seams. Prevents root damage and structural deterioration in municipal sidewalks and commercial hardscapes.',
    icon: (
      <svg className="w-6 h-6 text-green-700" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z" />
      </svg>
    ),
  },
  {
    title: 'Cable Strips, Medians & Guardrails',
    description: 'Targeted vegetation control for utility cable strips, road medians, fence lines, and guardrail zones where mechanical maintenance is impractical or creates safety hazards.',
    icon: (
      <svg className="w-6 h-6 text-green-700" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 3v18M18 3v18M6 12h12M3 6h3M3 18h3M18 6h3M18 18h3M12 6v3M12 15v3" />
      </svg>
    ),
  },
];

const ServicesPage = () => {
  const plans = useSelector(selectPlans);

  return (
    <div className='bg-custom-earth'>
      <Helmet>
        <title>Herbicide Application Services BC | Boultbee</title>
        <meta name="description" content="Bare ground, turf, noxious weed, and aquatic herbicide services for industrial and municipal clients across BC. IPM compliant." />
        <link rel="canonical" href="https://www.weedmanager.com/services" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Boultbee Vegetation Management" />
        <meta property="og:title" content="Herbicide Application Services BC | Boultbee" />
        <meta property="og:description" content="Bare ground, turf, noxious weed, and aquatic herbicide services for industrial and municipal clients across BC. IPM compliant." />
        <meta property="og:url" content="https://www.weedmanager.com/services" />
        <meta property="og:image" content="https://www.weedmanager.com/images/og-services.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Herbicide Application Services BC | Boultbee" />
        <meta name="twitter:description" content="Bare ground, turf, noxious weed, and aquatic herbicide services for industrial and municipal clients across BC. IPM compliant." />
        <meta name="twitter:image" content="https://www.weedmanager.com/images/og-services.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.weedmanager.com/" },
              { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.weedmanager.com/services" }
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify([
            {
              "@context": "https://schema.org", "@type": "Service",
              "serviceType": "Turf Weed Management",
              "provider": { "@type": "LocalBusiness", "name": "Boultbee Vegetation Management", "url": "https://www.weedmanager.com" },
              "areaServed": { "@type": "AdministrativeArea", "name": "British Columbia, Canada" },
              "description": "Selective herbicide programs that eliminate broadleaf weeds and invasive grasses while preserving healthy turf on sportsfields, parks, and commercial properties."
            },
            {
              "@context": "https://schema.org", "@type": "Service",
              "serviceType": "Bare Ground Vegetation Control",
              "provider": { "@type": "LocalBusiness", "name": "Boultbee Vegetation Management", "url": "https://www.weedmanager.com" },
              "areaServed": { "@type": "AdministrativeArea", "name": "British Columbia, Canada" },
              "description": "Total vegetation elimination for industrial areas using pre-emergent and post-emergent herbicide programs for rail yards, substations, gravel pads, and tank farms.",
              "hasOfferCatalog": {
                "@type": "OfferCatalog", "name": "Bare Ground Services",
                "itemListElement": [
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Total Vegetation Control" } },
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Season-Long Residual Program" } },
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Pre-Emergent Herbicide Application" } },
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Post-Emergent Spot Treatment" } }
                ]
              }
            },
            {
              "@context": "https://schema.org", "@type": "Service",
              "serviceType": "Noxious & Invasive Weed Control",
              "provider": { "@type": "LocalBusiness", "name": "Boultbee Vegetation Management", "url": "https://www.weedmanager.com" },
              "areaServed": { "@type": "AdministrativeArea", "name": "British Columbia, Canada" },
              "description": "Targeted herbicide treatment for BC-regulated noxious and invasive species including knapweed, Canada thistle, Scotch broom, knotweed, and purple loosestrife."
            },
            {
              "@context": "https://schema.org", "@type": "Service",
              "serviceType": "Aquatic Vegetation Management",
              "provider": { "@type": "LocalBusiness", "name": "Boultbee Vegetation Management", "url": "https://www.weedmanager.com" },
              "areaServed": { "@type": "AdministrativeArea", "name": "British Columbia, Canada" },
              "description": "Specialized herbicide application for controlling aquatic vegetation in drainage ditches and retention ponds using BC-approved products."
            },
            {
              "@context": "https://schema.org", "@type": "Service",
              "serviceType": "High-Rail Herbicide Application",
              "provider": { "@type": "LocalBusiness", "name": "Boultbee Vegetation Management", "url": "https://www.weedmanager.com" },
              "areaServed": { "@type": "AdministrativeArea", "name": "British Columbia, Canada" },
              "description": "Railroad-specific vegetation management using hi-rail equipped spray vehicles for ballast weed control, yard vegetation, and right-of-way herbicide programs."
            },
            {
              "@context": "https://schema.org", "@type": "Service",
              "serviceType": "Specialty & Infrastructure Weed Control",
              "provider": { "@type": "LocalBusiness", "name": "Boultbee Vegetation Management", "url": "https://www.weedmanager.com" },
              "areaServed": { "@type": "AdministrativeArea", "name": "British Columbia, Canada" },
              "description": "Precision herbicide application for sidewalks, brickwork, cable strips, medians, fence lines, and guardrails with GPS-tracked equipment."
            }
          ])}
        </script>
      </Helmet>
      <div className='wood-container'>
        <div className="min-h-screen flex flex-col">

          <PageHero
            title="Our Services"
            subtitle="Industrial-strength herbicide programs tailored to your site, your industry, and BC's regulatory requirements."
            backgroundImage={servicesHero}
          />

          {/* Core Service Cards — alternating image left/right */}
          {plans.map((plan, index) => (
            <ServiceCard
              key={plan.id}
              title={plan.title}
              description={plan.description}
              deliverables={plan.deliverables}
              imageSrc={serviceImages[index].src}
              altText={serviceImages[index].alt}
              imageWidth={serviceImages[index].width}
              imageHeight={serviceImages[index].height}
              imageOnRight={index % 2 === 1}
            />
          ))}

          {/* Specialty & Infrastructure Services Grid */}
          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur" style={{ maxWidth: '64rem' }}>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-2">Specialty & Infrastructure Services</h2>
              <p className="text-gray-700 text-center mb-6">Beyond our core programs, we provide targeted herbicide application for specialized infrastructure and environments.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {specialtyServices.map((service) => (
                  <Link key={service.title} to="/contact" className="block bg-white/80 rounded-lg p-5 border border-gray-200 hover-lift">
                    <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center mb-3">
                      {service.icon}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-gray-700 text-sm mb-3">{service.description}</p>
                    <span className="text-green-700 text-sm font-semibold">Get a Quote &rarr;</span>
                  </Link>
                ))}
              </div>
            </div>
          </AnimateOnScroll>

          {/* Bottom CTA */}
          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur text-center">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Need a Custom Program?</h2>
              <p className="text-gray-700 mb-5">Every site is different. Contact us for a free assessment and a program tailored to your property.</p>
              <Link to="/contact" className="cta-button">
                Request a Free Site Assessment
              </Link>
            </div>
          </AnimateOnScroll>

        </div>
      </div>
    </div>
  );
};

export default ServicesPage;
